import { KeyHelper } from "@privacyresearch/libsignal-protocol-typescript";
import type {
  KeyPairType,
  PreKeyPairType,
  SignedPreKeyPairType,
} from "@privacyresearch/libsignal-protocol-typescript";
import type { SignalProtocolStore } from "../store/SignalProtocolStore";
import type { OneTimePreKey, UploadBundleRequest } from "../types";
import { uploadBundle } from "./kds_client";
import { WEB_DEVICE_ID } from "./session";

const DEFAULT_OPK_COUNT = 50;
// String form of WEB_DEVICE_ID — uploadBundle takes a path-segment string,
// not a numeric id. Single source of truth still lives in crypto/session.ts.
const DEFAULT_DEVICE_ID = String(WEB_DEVICE_ID);

function ab2b64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

export interface BootstrapResult {
  registrationId: number;
  identityKeyPair: KeyPairType;
  signedPreKey: SignedPreKeyPairType;
  preKeys: PreKeyPairType[];
}

export async function generateAndStoreKeys(
  store: SignalProtocolStore,
  opkCount = DEFAULT_OPK_COUNT,
): Promise<BootstrapResult> {
  const registrationId = KeyHelper.generateRegistrationId();
  const identityKeyPair = await KeyHelper.generateIdentityKeyPair();
  const signedPreKey = await KeyHelper.generateSignedPreKey(
    identityKeyPair,
    1,
  );

  await store.setLocalRegistrationId(registrationId);
  await store.setIdentityKeyPair(identityKeyPair);

  await store.storeSignedPreKey(signedPreKey.keyId, signedPreKey.keyPair);

  const preKeys: PreKeyPairType[] = [];
  for (let i = 1; i <= opkCount; i++) {
    const preKey = await KeyHelper.generatePreKey(i);
    await store.storePreKey(preKey.keyId, preKey.keyPair);
    preKeys.push(preKey);
  }

  return { registrationId, identityKeyPair, signedPreKey, preKeys };
}

export async function bootstrapE2EE(
  store: SignalProtocolStore,
  token: string,
  deviceId = DEFAULT_DEVICE_ID,
): Promise<void> {
  const existingId = await store.getLocalRegistrationId();
  if (existingId !== undefined) {
    return;
  }

  const { registrationId, identityKeyPair, signedPreKey, preKeys } =
    await generateAndStoreKeys(store);

  const oneTimePreKeys: OneTimePreKey[] = preKeys.map((pk) => ({
    keyId: pk.keyId,
    publicKey: ab2b64(pk.keyPair.pubKey),
  }));

  const bundle: UploadBundleRequest = {
    registrationId,
    identityPubKey: ab2b64(identityKeyPair.pubKey),
    signedPreKey: {
      keyId: signedPreKey.keyId,
      publicKey: ab2b64(signedPreKey.keyPair.pubKey),
      signature: ab2b64(signedPreKey.signature),
    },
    oneTimePreKeys,
  };

  await uploadBundle(token, deviceId, bundle);
}

export async function regenerateAndUpload(
  store: SignalProtocolStore,
  token: string,
  deviceId = DEFAULT_DEVICE_ID,
): Promise<UploadBundleRequest> {
  await store.clearAll();
  const { registrationId, identityKeyPair, signedPreKey, preKeys } =
    await generateAndStoreKeys(store);

  const oneTimePreKeys: OneTimePreKey[] = preKeys.map((pk) => ({
    keyId: pk.keyId,
    publicKey: ab2b64(pk.keyPair.pubKey),
  }));

  const bundle: UploadBundleRequest = {
    registrationId,
    identityPubKey: ab2b64(identityKeyPair.pubKey),
    signedPreKey: {
      keyId: signedPreKey.keyId,
      publicKey: ab2b64(signedPreKey.keyPair.pubKey),
      signature: ab2b64(signedPreKey.signature),
    },
    oneTimePreKeys,
  };

  await uploadBundle(token, deviceId, bundle);

  // Returned so callers (e.g. settings UI) can verify the round-trip
  // against KDS field-by-field. The previous void return forced callers
  // to recompute the local bytes, which masked partial-write scenarios
  // where KDS kept a stale signedPreKey but accepted the new identityPubKey.
  return bundle;
}
